import React from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import SectionHeader from '../ui/SectionHeader';

interface FeatureCard {
  icon: string;
  title: string;
  desc: string;
}

const features: FeatureCard[] = [
  {
    icon: '🗄️',
    title: 'One data model. Every framework.',
    desc: 'Supplier data entered once flows automatically into PCF calculations, CCF reporting, and CBAM compliance. No reconciliation. No duplication. Every output from the same verified source.',
  },
  {
    icon: '♻️',
    title: 'Automated portfolio propagation.',
    desc: 'When a supplier updates a component\'s carbon data, every finished product containing it recalculates instantly. For companies with hundreds of SKUs, this eliminates weeks of rework per year.',
  },
  {
    icon: '🛡️',
    title: 'Built for regulators, used by humans.',
    desc: 'Actula reflects how CSRD, CBAM, PCF, CCF, and BRSR actually work — the real frameworks, the real data requirements, the real audit expectations. Not how a generic SaaS tool approximates them.',
  },
  {
    icon: '📰',
    title: 'Audit-ready by design.',
    desc: 'Every data point is timestamped, sourced, and traceable to its origin. External validators join the workflow directly. Your compliance data is always ready for scrutiny — not assembled at reporting time.',
  },
  {
    icon: '🧩',
    title: 'Modular. No big-bang implementation.',
    desc: 'Start with CBAM if January 2026 is the most urgent deadline. Start with ESG Data Collection if CSRD supplier data is the gap. Add modules as your compliance surface grows.',
  },
  {
    icon: '🌍',
    title: 'Global by design.',
    desc: 'BRSR for Indian listed companies. The full EU regulatory stack — CSRD, ESRS, EU Taxonomy, CBAM — for European and EU-trading enterprises. One platform for both regions.',
  },
];

export default function WhyActula(): React.ReactElement {
  const ref = useScrollReveal();

  return (
    <section
      className="differentiators section-pad"
      id="differentiators"
      ref={ref as React.RefObject<HTMLElement>}
    >
      <div className="container">
        <SectionHeader
          eyebrow="Why Actula"
          title="Purpose-built. Not repurposed."
          description="ESG Triage Technologies was founded by a team that spent fifteen years inside enterprise procurement and SAP Ariba implementations. We built Actula to be the infrastructure we wished existed."
        />
        <div className="features-grid">
          {features.map((f, i) => (
            <div key={i} className={`feature-card reveal reveal-delay-${(i % 3) + 1}`}>
              <span className="feature-card-icon">{f.icon}</span>
              <p className="feature-card-title">{f.title}</p>
              <p className="feature-card-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
